import React, { useEffect, useRef, useState } from "react";
import "./CorporateGovernance.css";
import Testimonial from "../../../Testimonial/Testimonial";
import { Helmet } from "react-helmet";

const CorporateGovernance = () => {
  const techItemsRow1 = [
    { name: "Angular.js", logo: "/logos/angular.png" },
    { name: "HTML", logo: "/logos/html.png" },
    { name: "CSS3", logo: "/logos/css.png" },
    { name: "Bootstrap", logo: "/logos/bootstrap.png" },
    { name: "Javascript", logo: "/logos/javascript.png" },
    { name: "AmazonDB", logo: "/logos/amazon_dynamodb.png" },
    { name: "Appian", logo: "/logos/appian.png" },
    { name: "Laravel", logo: "/logos/laravel.png" },
    { name: "MariaDB", logo: "/logos/mariadb.png" },
    { name: "MongoDB", logo: "/logos/mongodb.png" },
    { name: "Android", logo: "/logos/android-5.png" },
    { name: "ERC-20", logo: "/logos/erc-20.png" },
    { name: "Ethereum", logo: "/logos/ethereum.png" },
    { name: "Flutter", logo: "/logos/flutter.png" },
    { name: "Ionic", logo: "/logos/ionic.png" },
    {
      name: "Artificial Intelligence",
      logo: "/logos/artificial-intelligence.png",
    },
    { name: "Augmented Reality", logo: "/logos/Augmented Reality.png" },
    { name: "Crda", logo: "/logos/crda.png" },
    { name: "Data Science", logo: "/logos/data-science.png" },
    { name: "Go Chain", logo: "/logos/gochain.png" },
  ];

  const techItemsRow2 = [
    { name: "Sass", logo: "/logos/saas.png" },
    { name: "JQuery", logo: "/logos/jquery.png" },
    { name: "Less", logo: "/logos/less.png" },
    { name: "Codeigniter", logo: "/logos/codeignitor.png" },
    { name: "Django", logo: "/logos/django.png" },
    { name: "Node JS", logo: "/logos/node js.png" },
    { name: "PHP", logo: "/logos/php.png" },
    { name: "Salesforce", logo: "/logos/salesforce.png" },
    { name: "SQL Server", logo: "/logos/sql-server.png" },
    { name: "MySQL", logo: "/logos/mysql-5.png" },
    { name: "IOS", logo: "/logos/ios.png" },
    { name: "Oracle 6", logo: "/logos/oracle.png" },
    { name: "PostgreSQL", logo: "/logos/postgresql.png" },
    { name: "React Native", logo: "/logos/react_native.png" },
    { name: "Solidity", logo: "/logos/Solidity.png" },
    { name: "Hyperledger", logo: "/logos/Hyperledger.png" },
    { name: "Internet Of Things", logo: "/logos/iot.png" },
    { name: "One Ledger", logo: "/logos/OneLedger.png" },
    { name: "Virtual Reality", logo: "/logos/virtual-reality.png" },
  ];

  const duplicatedRow1 = [...techItemsRow1, ...techItemsRow1];
  const duplicatedRow2 = [...techItemsRow2, ...techItemsRow2];

  const containerRef = useRef(null);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const animationRef = useRef();

  useEffect(() => {
    // Set initial container width
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }

    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const scrollSpeed = 1; // Adjust speed as needed (pixels per frame)

    const animate = () => {
      setOffset((prevOffset) => {
        const maxOffset = row1Ref.current?.scrollWidth / 2 || 0;
        const newOffset = prevOffset + scrollSpeed;
        return newOffset >= maxOffset ? 0 : newOffset;
      });
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [containerWidth]);

  return (
    <div>
      <Helmet>
        <title>
          Corporate Governance Technology | Compliance & Risk Management
          Solutions
        </title>
        <meta
          name="description"
          content="Comprehensive corporate governance technology solutions including compliance management systems, board portal software, and risk assessment platforms for modern enterprises."
        />
        <meta
          name="keywords"
          content="corporate governance technology, compliance management, board portal software, risk assessment, governance platforms, regulatory compliance, ESG reporting, audit management, policy management, corporate compliance software"
        />
      </Helmet>
      <section className="mk-gov-intro">
        <div className="mk-gov-content-wrap">
          <h2 className="mk-gov-section-title">Our Governance Philosophy</h2>
          <p className="mk-gov-description">
            At MK Etech IT Software Company, we believe that strong corporate
            governance is fundamental to our success and sustainability. Our
            governance framework ensures accountability, fairness, and
            transparency in all our business practices.
          </p>
        </div>
      </section>

      <section className="mk-gov-principles">
        <div className="mk-gov-content-wrap">
          <h2 className="mk-gov-section-title">Governance Principles</h2>
          <div className="mk-gov-cards">
            <div className="mk-gov-card mk-card-1">
              <div className="mk-card-icon">
                <i className="fas fa-balance-scale"></i>
              </div>
              <h3 className="mk-card-title-corporate">Accountability</h3>
              <p className="mk-card-desc">
                Clear assignment of responsibilities and decision-making
                authorities to ensure accountability at all levels.
              </p>
            </div>

            <div className="mk-gov-card mk-card-2">
              <div className="mk-card-icon">
                <i className="fas fa-shield-alt"></i>
              </div>
              <h3 className="mk-card-title-corporate">Transparency</h3>
              <p className="mk-card-desc">
                Timely, accurate disclosure of information to stakeholders about
                our financial performance and corporate governance.
              </p>
            </div>

            <div className="mk-gov-card mk-card-3">
              <div className="mk-card-icon">
                <i className="fas fa-gavel"></i>
              </div>
              <h3 className="mk-card-title-corporate">Fairness</h3>
              <p className="mk-card-desc">
                Equitable treatment of all shareholders, including minority and
                foreign shareholders.
              </p>
            </div>

            <div className="mk-gov-card mk-card-4">
              <div className="mk-card-icon">
                <i className="fas fa-users"></i>
              </div>
              <h3 className="mk-card-title-corporate">Responsibility</h3>
              <p className="mk-card-desc">
                Active oversight of management and corporate strategy by our
                Board of Directors.
              </p>
            </div>

            <div className="mk-gov-card mk-card-5">
              <div className="mk-card-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <h3 className="mk-card-title-corporate">Sustainability</h3>
              <p className="mk-card-desc">
                Commitment to long-term value creation while considering
                environmental and social impacts.
              </p>
            </div>

            <div className="mk-gov-card mk-card-6">
              <div className="mk-card-icon">
                <i className="fas fa-lightbulb"></i>
              </div>
              <h3 className="mk-card-title-corporate">Innovation</h3>
              <p className="mk-card-desc">
                Fostering a culture of continuous improvement and technological
                advancement.
              </p>
            </div>

            <div className="mk-gov-card mk-card-7">
              <div className="mk-card-icon">
                <i className="fas fa-handshake"></i>
              </div>
              <h3 className="mk-card-title-corporate">Ethics</h3>
              <p className="mk-card-desc">
                Maintaining the highest ethical standards in all business
                dealings and decisions.
              </p>
            </div>

            <div className="mk-gov-card mk-card-8">
              <div className="mk-card-icon">
                <i className="fas fa-clipboard-check"></i>
              </div>
              <h3 className="mk-card-title-corporate">Compliance</h3>
              <p className="mk-card-desc">
                Adherence to all applicable laws, regulations, and internal
                policies.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mk-gov-structure">
        <div className="mk-gov-content-wrap">
          <h2 className="mk-gov-section-title">Governance Structure</h2>
          <div className="mk-gov-structure-content">
            <div className="mk-gov-structure-text">
              <p>
                Our governance structure is designed to facilitate effective
                decision-making and appropriate oversight. The Board of
                Directors provides strategic guidance and oversight, while
                various committees focus on specific areas such as audit,
                compensation, and nomination.
              </p>
              <ul className="mk-gov-structure-list">
                <li>Board of Directors</li>
                <li>Audit Committee</li>
                <li>Compensation Committee</li>
                <li>Nominating and Governance Committee</li>
                <li>Risk Management Committee</li>
              </ul>
            </div>
            <div className="mk-gov-structure-visual">
              <div className="mk-gov-hierarchy">
                <div className="mk-hierarchy-top">Shareholders</div>
                <div className="mk-hierarchy-connector"></div>
                <div className="mk-hierarchy-middle">Board of Directors</div>
                <div className="mk-hierarchy-connector"></div>
                <div className="mk-hierarchy-bottom">
                  <div className="mk-hierarchy-item">Audit Committee</div>
                  <div className="mk-hierarchy-item">
                    Compensation Committee
                  </div>
                  <div className="mk-hierarchy-item">Governance Committee</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mk-gov-policies">
        <div className="mk-gov-content-wrap">
          <h2 className="mk-gov-section-title">Governance Policies</h2>
          <div className="mk-gov-policies-grid">
            <div className="mk-policy-item">
              <h3 className="mk-policy-title">Code of Conduct</h3>
              <p className="mk-policy-desc">
                Our Code of Conduct establishes the ethical standards and
                principles that guide our business decisions and actions.
              </p>
            </div>

            <div className="mk-policy-item">
              <h3 className="mk-policy-title">Whistleblower Policy</h3>
              <p className="mk-policy-desc">
                A mechanism for employees to report concerns about unethical
                behavior without fear of retaliation.
              </p>
            </div>

            <div className="mk-policy-item">
              <h3 className="mk-policy-title">Board Charter</h3>
              <p className="mk-policy-desc">
                Defines the roles, responsibilities, and operations of our Board
                of Directors.
              </p>
            </div>

            <div className="mk-policy-item">
              <h3 className="mk-policy-title">Data Protection Policy</h3>
              <p className="mk-policy-desc">
                Guidelines for handling and protecting sensitive company and
                customer data in compliance with regulations.
              </p>
            </div>

            <div className="mk-policy-item">
              <h3 className="mk-policy-title">Risk Management Policy</h3>
              <p className="mk-policy-desc">
                Framework for identifying, assessing, and mitigating business
                risks across the organization.
              </p>
            </div>

            <div className="mk-policy-item">
              <h3 className="mk-policy-title">Sustainability Policy</h3>
              <p className="mk-policy-desc">
                Our commitment to environmental responsibility and sustainable
                business practices.
              </p>
            </div>

            <div className="mk-policy-item">
              <h3 className="mk-policy-title">Diversity & Inclusion Policy</h3>
              <p className="mk-policy-desc">
                Guidelines for promoting diversity, equity, and inclusion in our
                workplace and business practices.
              </p>
            </div>

            <div className="mk-policy-item">
              <h3 className="mk-policy-title">IT Security Policy</h3>
              <p className="mk-policy-desc">
                Protocols and procedures for maintaining the security of our IT
                infrastructure and digital assets.
              </p>
            </div>

            <div className="mk-policy-item">
              <h3 className="mk-policy-title">Remote Work Policy</h3>
              <p className="mk-policy-desc">
                Guidelines and expectations for employees working remotely to
                ensure productivity and security.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Testimonial />

      <div className="tech-slider-container" ref={containerRef}>
        <h2 className="tech-slider-title">Tools & Tech Stack</h2>
        <div className="tech-slider-divider"></div>

        {/* First row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row1Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow1.map((tech, index) => (
              <div key={`row1-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Second row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row2Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow2.map((tech, index) => (
              <div key={`row2-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorporateGovernance;
